import jsPDF from 'jspdf'

/**
 * Gets the pdf size (width, height) and the initial position on it (x, y)
 *
 * @param pdf
 * @param margin
 * @returns
 */
export const getPdfDimensions = (pdf: jsPDF, margin: number): { width: number; height: number; x: number; y: number } => {
  const width = pdf.internal.pageSize.width * (1 - margin)
  const height = pdf.internal.pageSize.height * (1 - margin)

  const x = pdf.internal.pageSize.width * (margin / 2)
  const y = pdf.internal.pageSize.height * (margin / 2)

  return { width, height, x, y }
}

/**
 * Calculates the ratio of an element (e.g. image or pdf).
 * E.g. can be used to resize an image without changing the image ratio
 *
 * @param pdfWidth
 * @param pdfHeight
 * @param element
 * @returns
 */
export const calculateRatio = (pdfWidth: number, pdfHeight: number, element: HTMLElement | null): number => {
  const widthRatio = pdfWidth / (element?.clientWidth ?? 1)
  const heightRatio = pdfHeight / (element?.clientHeight ?? 1)
  return Math.min(widthRatio, heightRatio)
}

/**
 * Calculates where to put an image on the pdf (x,y) and with what size (width, height)
 *
 * @param currentX currently used x position where the image starts
 * @param currentY current y position of the pdf's last content
 * @param paddingTop padding to the pdf's last content
 * @param ratio image ratio
 * @param element image
 * @returns
 */
export const calculateImageDimensions = (
  currentX: number,
  currentY: number,
  paddingTop: number,
  ratio: number,
  element: HTMLElement | null,
  resizeFactor: number
): { x: number; y: number; width: number; height: number } => {
  const x = currentX
  const y = currentY + paddingTop
  let width = 0
  let height = 0

  if (element?.clientWidth && element?.clientHeight) {
    width = element?.clientWidth * ratio * resizeFactor
    height = element?.clientHeight * ratio * resizeFactor
  }

  return { x, y, width, height }
}

/**
 * Adjusts the dimensions in case an image is too big and should be reduced to properly fit on the pdf
 *
 * @param height
 * @param width
 * @param maxHeight
 * @param maxWidth
 * @returns
 */
export const calculateAdjustedDimensions = (
  height: number,
  width: number,
  maxHeight: number,
  maxWidth: number
): { height: number; width: number } => {
  if (height <= maxHeight && width <= maxWidth) return { height, width }

  const aspectRatio = width / height

  let newHeight = maxHeight
  let newWidth = newHeight * aspectRatio

  if (newWidth > maxWidth) {
    newWidth = maxWidth
    newHeight = newWidth / aspectRatio
  }

  return {
    height: newHeight,
    width: newWidth,
  }
}
